import React from 'react';
import { PerformanceSummary } from './types';

interface PerformanceTrendsProps {
  performanceSummary: Pick<PerformanceSummary, 'accuracy_trend' | 'speed_trend' | 'consistency_score' | 'learning_velocity' | 'strongest_overall_subject' | 'weakest_overall_subject' | 'preferred_difficulty' | 'most_active_day' | 'most_active_time' | 'quiz_frequency_per_week'>;
}

const PerformanceTrends: React.FC<PerformanceTrendsProps> = ({ performanceSummary }) => {
  // Helper function to parse numeric values from strings
  const parseNumber = (value: string | number): number => {
    if (typeof value === 'number') return value;
    return parseFloat(value) || 0;
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return '📈';
      case 'declining':
        return '📉';
      case 'stable':
        return '➡️';
      default:
        return '📊';
    }
  };

  return (
    <div className="row mb-4">
      <div className="col-md-4 mb-3">
        <div className="card h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>
              Performance Trends
            </h5>
          </div>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>Accuracy Trend:</span>
              <span className="badge bg-primary">
                {getTrendIcon(performanceSummary.accuracy_trend)} {performanceSummary.accuracy_trend}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>Speed Trend:</span>
              <span className="badge bg-info">
                {getTrendIcon(performanceSummary.speed_trend)} {performanceSummary.speed_trend}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>Consistency:</span>
              <span className="badge bg-success">
                {parseNumber(performanceSummary.consistency_score).toFixed(1)}%
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span>Learning Velocity:</span>
              <span className="badge bg-warning text-dark">
                {performanceSummary.learning_velocity}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-3">
        <div className="card h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-book me-2"></i>
              Subject Performance
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <strong className="text-success">Strongest Subject:</strong>
              <br />
              <span className="badge bg-success mt-1">
                {performanceSummary.strongest_overall_subject || 'Not determined'}
              </span>
            </div>
            <div className="mb-3">
              <strong className="text-danger">Focus Area:</strong>
              <br />
              <span className="badge bg-danger mt-1">
                {performanceSummary.weakest_overall_subject || 'Not determined'}
              </span>
            </div>
            <div>
              <strong className="text-info">Preferred Difficulty:</strong>
              <br />
              <span className="badge bg-info mt-1">
                {performanceSummary.preferred_difficulty}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-3">
        <div className="card h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-calendar-alt me-2"></i>
              Activity Patterns
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <strong>Most Active Day:</strong>
              <br />
              <span className="badge bg-primary mt-1">
                {performanceSummary.most_active_day || 'Not determined'}
              </span>
            </div>
            <div className="mb-3">
              <strong>Preferred Time:</strong>
              <br />
              <span className="badge bg-info mt-1">
                {performanceSummary.most_active_time || 'Not determined'}
              </span>
            </div>
            <div>
              <strong>Weekly Frequency:</strong>
              <br />
              <span className="badge bg-success mt-1">
                {performanceSummary.quiz_frequency_per_week} quizzes/week
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTrends;